import { useTranslation } from "react-i18next";
import { Icon, Search } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";
import { useSearchCustomerByCarQuery } from "modules/Appointments/components/NewAppointment/hooks";
import { DEBOUNCED_SEARCH_TIME, MIN_SEARCH_LENGTH, getCustomerOrCompanyName, getCustomerTypeLabel } from "modules/Appointments/components/NewAppointment/utils";
import { debounce, isKeyLoopLocation } from "utils/common";

import type { Customer } from "models";
import type { AppointmentSection } from "modules/Appointments/components/NewAppointment";
import type { SetStateAction } from "react";
import type { SearchResultData } from "semantic-ui-react";

type CustomerResultSelectType = {
  customer?: Customer;
  customerType?: CUSTOMER_TYPES;
};

type CustomerSearchResultItemProps = {
  customerTypeLabel: string | null;
  customerOrCompanyName: string;
};

type CustomerSearchProps = {
  customerSearchTerm: string;
  showCustomerType: string | null;
  isCustomerSearchByNameLoading: boolean;
  isDisabledOwnerSearch: boolean;
  isDisabledDriverSearch: boolean;
  isDisabledContractorSearch: boolean;
  activeCustomerTab: CUSTOMER_TYPES;
  customersByName?: Customer[];
  refetchCustomersByName: () => void;
  setCustomerSearchTerm: (value: SetStateAction<string>) => void;
  setShowCustomerType: (value: SetStateAction<string | null>) => void;
  handleCustomerResultSelect: ({ customer, customerType }: CustomerResultSelectType) => void;
  handleCustomerTabChange: (value: CUSTOMER_TYPES) => void;
  setSelectedCustomer: (value: SetStateAction<Customer | null | undefined>) => void;
};

const CustomerSearchResultItem = ({ customerOrCompanyName, customerTypeLabel }: CustomerSearchResultItemProps) => (
  <div className="NewAppointment__SearchResult">
    <p>{customerOrCompanyName}</p>
    <div className="NewAppointment__SearchResult__tag-container">{customerTypeLabel && <div className="NewAppointment__SearchResult__tag">{customerTypeLabel}</div>}</div>
  </div>
);

const FormHelper = createFormHelper<AppointmentSection>();

export const CustomerSearch = ({
  showCustomerType,
  customerSearchTerm,
  isCustomerSearchByNameLoading,
  setShowCustomerType,
  setSelectedCustomer,
  handleCustomerTabChange,
  setCustomerSearchTerm,
  handleCustomerResultSelect,
  refetchCustomersByName,
  customersByName,
  isDisabledContractorSearch,
  isDisabledDriverSearch,
  isDisabledOwnerSearch,
  activeCustomerTab
}: CustomerSearchProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { watch } = FormHelper.useFormContext();
  const regNr = watch("car.reg_nr");
  const { data: customersByCar, isFetching: isCustomerSearchByCarLoading } = useSearchCustomerByCarQuery(regNr);

  const keyloopLocation = isKeyLoopLocation(selectedLocation);

  const formatCustomer = (customer: Customer) => ({
    id: `${customer.dms_nr}-${customer.customer_type}`,
    key: `${customer.dms_nr}-${customer.customer_type}`,
    title: `${customer.title || ""} ${customer.firstname || ""} ${customer.surname || ""} - ${customer.place || ""}`,
    customer
  });

  const getIsSearchDisabled = () => {
    if (!keyloopLocation) return false;

    if (activeCustomerTab === CUSTOMER_TYPES.Driver) return isDisabledDriverSearch;
    if (activeCustomerTab === CUSTOMER_TYPES.Contractor) return isDisabledContractorSearch;
    if (activeCustomerTab === CUSTOMER_TYPES.Owner) return isDisabledOwnerSearch;

    return false;
  };

  const handleCustomerSearch = (searchTerm?: string) => {
    if (!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH) return;
    refetchCustomersByName();
  };

  const debouncedCustomerSearch = debounce((value: string) => handleCustomerSearch(value), DEBOUNCED_SEARCH_TIME);

  const handleCustomerSearchChange = (value: string) => {
    setCustomerSearchTerm(value);
    debouncedCustomerSearch(value);
  };

  const handleCustomerOnSearchChange = (value: string | undefined) => {
    handleCustomerSearchChange(String(value));
    setShowCustomerType(null);
  };

  const handleCustomerResultSearchSelect = (data: SearchResultData) => handleCustomerResultSelect({ customer: data.result.customer });

  const handleCustomerOnResultSelect = (data: SearchResultData) => {
    const selectedCustomer = getCustomerOrCompanyName({
      company: data.result.customer.company,
      title: data.result.customer.title,
      firstname: data.result.customer.firstname,
      surname: data.result.customer.surname
    });

    handleCustomerResultSearchSelect(data);
    setSelectedCustomer(data.result.customer);
    setCustomerSearchTerm(selectedCustomer);
    if (data.result.customer.customer_type) handleCustomerTabChange(data.result.customer.customer_type);
    setShowCustomerType(getCustomerTypeLabel(t, data.result.customer.customer_type));
  };

  const formattedCustomersByCarForDropdown = customersByCar?.map(formatCustomer) || [];
  const formattedCustomersByNameForDropdown = customersByName?.map(formatCustomer) || [];

  return (
    <div className="search-wrapper">
      <Search
        fluid
        disabled={!regNr || getIsSearchDisabled() || isCustomerSearchByCarLoading}
        minCharacters={0}
        value={customerSearchTerm}
        loading={isCustomerSearchByNameLoading || isCustomerSearchByCarLoading}
        results={customerSearchTerm ? formattedCustomersByNameForDropdown : formattedCustomersByCarForDropdown}
        icon={<Icon className="magnifying glass" />}
        noResultsMessage={t("v8_no_results", "No results found")}
        onResultSelect={(_, data) => handleCustomerOnResultSelect(data)}
        onSearchChange={(_e, { value }) => handleCustomerOnSearchChange(value)}
        resultRenderer={({ customer: { title, firstname, surname, customer_type, company } }) => (
          <CustomerSearchResultItem
            customerTypeLabel={getCustomerTypeLabel(t, customer_type)}
            customerOrCompanyName={getCustomerOrCompanyName({ company, title, firstname, surname })}
          />
        )}
      />
      {showCustomerType && <div className="customer-tags">{showCustomerType && <div className="customer-tag">{showCustomerType}</div>}</div>}
    </div>
  );
};
